import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";

import ViewIcon from "../icons/ViewIcon";
import CodeIcon from "../icons/CodeIcon";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import { DownloadIcon } from "../icons";

import { ChatHelper } from "../helpers/chatHelper";
import type { IFileAttachment, IPageRotation } from "../../types/chat";
import { EMRViewer, PdfViewer, EdiClaimViewer } from "../viewers";
import { detectDocumentType, isEdiFile } from "../../services/fileParser";
import HeaderActions from "./HeaderActions";

import ImageViewer, { resolveImageSrc } from "./ImageViewer";
import DocxViewer from "./DocxViewer";
import TxtViewer from "./TxtViewer";
import CsvViewer from "./CsvViewer";
import XlsxViewer from "./XlsxViewer";
import HtmlViewer from "./HtmlViewer";
import TiffViewer from "./TiffViewer";
import ImageHelper, { IMAGE_FILE_RE } from "../helpers/imageHelper";
import { isTiffPreviewPath } from "../helpers/tiffHelper";
import { useCapabilities } from "../../contexts/permissions/useCapabilities";

interface DocumentViewerProps {
    // Scroll to page
    initialPage?: number | null;
    rotation?: IPageRotation;
    fileName?: string;
    displayName?: string;   // Keep this propertry only for display, don't change entire flow
    fileContent?: string;
    fileContentType?: string;
    rawContent?: string; // Raw content for "Raw" tab (e.g., raw EDI file content)
    fileAttachment?: IFileAttachment | null;
    position: "left" | "right";
    onClose: () => void;
    onBack?: () => void;
    canGoBack?: boolean;
    onTogglePosition: () => void;
    onHeaderHeightChange?: (h: number) => void;
    isLoading?: boolean;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    initialPage,
    rotation,
    fileName,
    displayName,
    fileContent,
    fileContentType,
    rawContent,
    fileAttachment,
    onClose,
    onBack,
    canGoBack = false,
    onHeaderHeightChange,
    isLoading = false,
}) => {
    const { canDownloadAttachments } = useCapabilities();
    const [activeTab, setActiveTab] = useState<'structured' | 'raw'>('structured');
    
    // Reset to structured view when file changes
    useEffect(() => {
        setActiveTab('structured');
    }, [fileName]);

    const detected = useMemo(() => detectDocumentType(fileName, fileContent), [fileName, fileContent]);
    const is835 = useMemo(() => {
        const ln = (fileName || '').toLowerCase();
        if (/\b835\b/.test(ln)) return true;
        if (detected.kind === 'edi' && detected.subtype === '835') return true;
        try {
            const parsed = JSON.parse(fileContent || '');
            // 835-specific JSON hints
            if (parsed && (parsed.remittanceSummary || parsed.summary || parsed.claim_adjustment || parsed.payment_information)) {
                return true;
            }
        } catch {}
        return false;
    }, [detected, fileContent, fileName]);
    // For "Raw" tab: use rawContent if available (for EDI files), otherwise use fileContent
    const rawDisplayContent = rawContent !== undefined ? rawContent : fileContent;
    let display: React.ReactNode = null;
    if (rawDisplayContent && rawDisplayContent.trim().length > 0) {
        try {
            const parsed = JSON.parse(rawDisplayContent);
            display = (
                <pre className="text-sm leading-relaxed whitespace-pre overflow-auto">
{JSON.stringify(parsed, null, 2)}
                </pre>
            );
        } catch {
            display = (
                <pre className="text-sm leading-relaxed whitespace-pre-wrap wrap-break-word overflow-auto">{rawDisplayContent}</pre>
            );
        }
    } else {
        display = <span>Preview will appear here.</span>;
    }
    const headerRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const report = () => {
            if (onHeaderHeightChange && headerRef.current) onHeaderHeightChange(headerRef.current.offsetHeight);
        };
        report();
        window.addEventListener('resize', report);
        return () => window.removeEventListener('resize', report);
    }, [onHeaderHeightChange]);

    const isPdfContent = useMemo(() => {
        const s = (fileContent || '').trim();
        const ln = (fileName || '').toLowerCase();
        if (!s) return false;
        if ((fileContentType || '').toLowerCase().startsWith('application/pdf')) return true;
        if (s.startsWith('data:application/pdf')) return true;
        if (s.startsWith('JVBERi0')) return true; // base64 PDF header
        // Only treat blob URLs as PDF if the file name hints it's a PDF
        if (s.startsWith('blob:') && ln.endsWith('.pdf')) return true;
        if (/^https?:\/\//i.test(s) && /\.pdf(\?|$)/i.test(s)) return true; // direct PDF URL
        return false;
    }, [fileContent, fileName, fileContentType]);

    const isImageContent = useMemo(() => {
        return ImageHelper.isImageContent(fileContent || '', fileContentType || '', fileName || '');
    }, [fileContent, fileContentType, fileName]);

    const imagePreviewSrc = useMemo(
        () => resolveImageSrc(fileName, fileContent, fileContentType),
        [fileName, fileContent, fileContentType],
    );

    const isDocxContent = useMemo(() => {
        const ln = (fileName || "").toLowerCase();
        const ct = (fileContentType || "").toLowerCase();
        if (ln.endsWith(".docx") || ln.endsWith(".doc")) return true;
        if (ct.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document")) {
            return true;
        }
        return false;
    }, [fileName, fileContentType]);

    const isLegacyDocContent = useMemo(() => {
        const ln = (fileName || "").toLowerCase();
        const ct = (fileContentType || "").toLowerCase();
        // .docx and .doc are both handled by isDocxContent (docx-preview rendering path)
        if (ln.endsWith(".docx") || ln.endsWith(".doc")) return false;
        if (ct.includes("application/msword")) return true;
        return false;
    }, [fileName, fileContentType]);

    const isPlainTextFile = useMemo(() => {
        const ln = (fileName || "")?.toLowerCase();
        const ct = (fileContentType || "")?.toLowerCase();

        return ln?.endsWith(".txt") || ct?.startsWith("text/plain")
    }, [fileName, fileContentType]);

    const isCsvFile = useMemo(() => {
        const ln = (fileName || "").toLowerCase();
        const ct = (fileContentType || "").toLowerCase();
        if (ln.endsWith(".csv")) return true;
        if (ct.startsWith("text/csv") || ct === "application/csv") return true;
        return false;
    }, [fileName, fileContentType]);

    const isXlsxFile = useMemo(() => {
        const ln = (fileName || "").toLowerCase();
        const ct = (fileContentType || "").toLowerCase();
        if (ln.endsWith(".xlsx") || ln.endsWith(".xls")) return true;
        if (
            ct.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
            ct.includes("application/vnd.ms-excel")
        ) {
            return true;
        }
        return false;
    }, [fileName, fileContentType]);

    const isHtmlFile = useMemo(() => {
        const ln = (fileName || "").toLowerCase();
        const ct = (fileContentType || "").toLowerCase();
        if (ln.endsWith(".html") || ln.endsWith(".htm")) return true;
        if (ct.startsWith("text/html") || ct.includes("application/xhtml+xml")) {
            return true;
        }
        return false;
    }, [fileName, fileContentType]);

    const isTiffFile = useMemo(() => {
        const fc = (fileContent || "").trim();
        if (!fc) {
            return false;
        }

        return isTiffPreviewPath(fc, fileName || "", fileContentType || "");
    }, [fileContent, fileName, fileContentType]);

    const hideStructuredRawTabs =
        detected.kind === 'pdf' ||
        isPdfContent ||
        (fileName || '').toLowerCase().endsWith('.pdf') ||
        isImageContent ||
        IMAGE_FILE_RE.test((fileName || '').toLowerCase()) ||
        isDocxContent ||
        isLegacyDocContent ||
        isPlainTextFile ||
        isCsvFile ||
        isXlsxFile ||
        isHtmlFile ||
        isTiffFile;

    return (
        <div className="flex flex-col h-full min-h-0 w-full border-x border-b border-hex-gray-300 overflow-hidden">
            <div ref={headerRef} className="flex items-center justify-between gap-2 p-3 border-b border-gray-200 shrink-0 flex-wrap">
                <div className="flex items-center gap-2">
                    {canGoBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className="flex items-center justify-center rounded-[8px] h-[28px] w-[28px] text-hex-gray-600 hover:bg-hex-gray-100 shrink-0"
                            title="Back"
                            aria-label="Back to previous view"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                    )}
                    {fileName && (
                        <span className="font-manrope text-md font-semibold text-chat-dark truncate max-w-document-max" title={fileName}>{displayName || fileName}</span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Only show tabs when we use structured/raw viewers */}
                    {!hideStructuredRawTabs && (
                        <div className="flex items-center bg-white border border-gray-300 rounded-full overflow-hidden">
                            <button 
                                className={`flex items-center gap-2 px-4 py-1 border-r border-gray-300 transition-colors  ${
                                    activeTab === 'structured' 
                                        ? 'bg-hex-safe-green text-white' 
                                        : 'bg-white text-hex-text-dark hover:bg-gray-50'
                                }`} 
                                onClick={() => setActiveTab('structured')} 
                                aria-label="Structured"
                            >
                                <ViewIcon />
                                <span>Structured</span>
                            </button>
                            <button 
                                className={`flex items-center gap-2 px-4 py-1 transition-colors ${
                                    activeTab === 'raw' 
                                        ? 'bg-hex-safe-green text-white' 
                                        : 'bg-white text-hex-text-dark hover:bg-gray-50'
                                }`} 
                                onClick={() => setActiveTab('raw')} 
                                aria-label="Raw"
                            >
                                <CodeIcon />
                                <span>Raw</span>
                            </button>
                        </div>
                    )}
                    {fileAttachment && canDownloadAttachments ? (
                        <Button
                            onClick={() => ChatHelper.downloadFile(fileAttachment)}
                            title="Download file"
                            sx={{
                                minWidth: 0,
                                width: "32px",
                                height: "32px",
                                padding: "10px",
                                borderRadius: "8px",
                                border: "1px solid",
                                borderColor: "grey.200",
                                backgroundColor: "background.paper",
                                boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
                                "&:hover": {
                                    backgroundColor: "grey.50",
                                    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
                                },
                            }}
                        >
                            <DownloadIcon size={13} />
                        </Button>
                    ) : null}
                    <HeaderActions
                        onClose={onClose}
                        closeTitle="Close viewer"
                    />
                </div>
            </div>
            
            {/* Loading state */}
            {isLoading && (
                <div className="flex-1 min-h-0 overflow-auto p-3 text-hex-gray-700 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-2xl text-hex-gray-400 mb-2">📄</div>
                        <div className="text-md text-hex-gray-500">Loading file...</div>
                    </div>
                </div>
            )}
            
            {/* For PDFs, always show the PDF viewer */}
            {!isLoading && (detected.kind === 'pdf' || isPdfContent || (fileName||'').toLowerCase().endsWith('.pdf')) ? (
                <div className="flex-1 min-h-0 min-w-0 p-2 text-hex-gray-700 flex flex-col">
                    <PdfViewer content={fileContent || ''} fileName={fileName} rotation={rotation} initialPage={initialPage} />
                </div>
            ) : !isLoading && isTiffFile ? (
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    <TiffViewer source={fileContent ?? ""} fileName={fileName} />
                </div>
            ) : !isLoading && imagePreviewSrc ? (
                <ImageViewer src={imagePreviewSrc} fileName={fileName} />
            ) : !isLoading && isCsvFile ? (
                <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                    <CsvViewer source={fileContent ?? ""} fileName={fileName} />
                </div>
            ) : !isLoading && isXlsxFile ? (
                <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
                    <XlsxViewer source={fileContent ?? ""} fileName={fileName} />
                </div>
            ) : !isLoading && isHtmlFile ? (
                <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                    <HtmlViewer source={fileContent ?? ""} fileName={fileName} />
                </div>
            ) : !isLoading && isPlainTextFile ? (
                <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                    <TxtViewer source={fileContent ?? ""} fileName={fileName} />
                </div>
            ) : !isLoading && (isDocxContent || isLegacyDocContent) && fileContent ? (
                <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                    <DocxViewer
                        source={fileContent}
                        fileName={fileName}
                        legacyDoc={(fileName || '').toLowerCase().endsWith('.doc') || isLegacyDocContent}
                    />
                </div>
            ) : !isLoading ? (
                <>
                    {/* For non-PDFs, show tabs and content */}
                    {activeTab === 'structured' && (
                        <div className="flex-1 min-h-0 overflow-auto p-3 text-hex-gray-700">
                            {(is835 || detected.kind === 'edi' || isEdiFile(fileName)) && fileContent ? (
                                <EdiClaimViewer content={fileContent} />
                            ) : detected.kind === 'fhir' ? (
                                <EMRViewer content={fileContent || ''} />
                            ) : (
                                <div className="text-md text-hex-gray-500">No structured renderer for this format yet. Switch to Raw.</div>
                            )}
                        </div>
                    )}
                    {activeTab === 'raw' && (
                        <div className="flex-1 min-h-0 overflow-auto p-3 text-hex-gray-700 text-lg">
                            {display}
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};

export default DocumentViewer;


